import { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Box,
  Card,
  CardContent,
  CardHeader,
  Grid,
  Alert,
  Button,
  Divider,
} from '@mui/material';
import {
  CheckCircle,
  Warning,
  Info,
  Description,
  CloudDone,
  Assignment,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API_CONFIG } from '../utils/config';
import { useAuth } from '../context/AuthContext';
import Lottie from 'lottie-react';
import loadingAnimation from '../assets/loading-animation.json';

const ZatcaDetails = () => {
  const [loading, setLoading] = useState(true);
  const [companyInfo, setCompanyInfo] = useState(null);
  const [error, setError] = useState(null);
  const navigate = useNavigate();
  const { currentTenant } = useAuth();

  const fetchCompanyInfo = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const tenantId = currentTenant?._id || currentTenant?.id;

      if (!tenantId) {
        setError('Company information not found');
        setLoading(false);
        return;
      }

      const response = await axios.post(
        `${API_CONFIG.ZATCA_API_URL}/zatca/company/info`,
        { companyId: tenantId },
        {
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          }
        }
      );

      if (response.data.success) {
        setCompanyInfo(response.data);
        setError(null);
      } else {
        setError('Failed to load ZATCA details');
      }
    } catch (err) {
      console.error('Error fetching ZATCA company info:', err);
      setError(err.response?.data?.message || 'Failed to load ZATCA details');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCompanyInfo();
  }, []);

  const handleUpgrade = () => {
    navigate('/zatca-onboarding');
  };

  if (loading) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: '#f8f9fa',
        }}
      >
        <Box sx={{ textAlign: 'center' }}>
          <Lottie 
            animationData={loadingAnimation} 
            loop={true}
            style={{ width: 200, height: 200, margin: '0 auto' }}
          />
        </Box>
      </Box>
    );
  }

  if (error) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          background: '#f8f9fa',
          py: 2,
          px: { xs: 1, md: 2 },
        }}
      >
        <Container maxWidth={false} sx={{ px: { xs: 1, sm: 2 } }}>
          <Alert severity="error" sx={{ borderRadius: 1 }}>
            {error}
          </Alert>
        </Container>
      </Box>
    );
  }

  const data = companyInfo?.data;
  const isOnboarded = companyInfo?.onboarded;
  const isPhase2 = data?.certificates?.hasProductionCSID;
  const canSubmit = data?.canSubmitInvoices;

  // If not onboarded at all, show a special message
  if (!isOnboarded) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          background: '#f8f9fa',
          py: 2,
          px: { xs: 1, md: 2 },
          width: '100%',
          overflow: 'auto',
        }}
      >
        <Container maxWidth={false} sx={{ px: { xs: 1, sm: 2 }, width: '100%' }}>
          {/* Header */}
          <Box sx={{ mb: 2 }}>
            <Typography
              variant="h5"
              component="h1"
              fontWeight="600"
              sx={{
                color: '#031b4e',
                mb: 0.5,
                fontSize: { xs: '1.25rem', md: '1.5rem' }
              }}
            >
              ZATCA Compliance Details
            </Typography>
            <Typography color="text.secondary" variant="body2" sx={{ fontSize: '0.875rem' }}>
              View your ZATCA e-invoicing compliance status and certificate information
            </Typography>
          </Box>

          {/* Not Onboarded Alert */}
          <Card elevation={1} sx={{ borderRadius: 1, bgcolor: '#ffebee', border: '1px solid #ef5350' }}>
            <CardContent sx={{ p: 4, textAlign: 'center' }}>
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
                <Warning sx={{ fontSize: '4rem', color: '#d32f2f' }} />
                <Typography variant="h6" sx={{ fontWeight: 600, color: '#d32f2f', fontSize: '1.125rem' }}>
                  Your Company is Not Onboarded with ZATCA Phase 2
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.875rem', maxWidth: 600 }}>
                  To enable ZATCA e-invoicing compliance and submit invoices to ZATCA, you need to complete the Phase 2 onboarding process. 
                  This will generate the necessary certificates and configure your system for compliance.
                </Typography>
                <Button
                  variant="contained"
                  size="large"
                  onClick={handleUpgrade}
                  sx={{
                    bgcolor: '#d32f2f',
                    '&:hover': { bgcolor: '#b71c1c' },
                    borderRadius: 1,
                    textTransform: 'none',
                    fontSize: '0.875rem',
                    fontWeight: 500,
                    px: 4,
                    py: 1.5,
                    mt: 2
                  }}
                >
                  Start Phase 2 Onboarding Now
                </Button>
              </Box>
            </CardContent>
          </Card>

          {/* Information Cards */}
          <Grid container spacing={2} sx={{ mt: 2 }}>
            <Grid item xs={12} md={4}>
              <Card elevation={1} sx={{ borderRadius: 1, height: '100%' }}>
                <CardContent sx={{ p: 2.5 }}>
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
                    <Info sx={{ color: '#1976d2', mt: 0.5 }} />
                    <Box>
                      <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5, fontSize: '0.875rem' }}>
                        What is Phase 2?
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.875rem' }}>
                        Phase 2 enables integration with ZATCA's e-invoicing platform, allowing you to submit invoices for clearance and reporting.
                      </Typography>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} md={4}>
              <Card elevation={1} sx={{ borderRadius: 1, height: '100%' }}>
                <CardContent sx={{ p: 2.5 }}>
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
                    <CheckCircle sx={{ color: '#2e7d32', mt: 0.5 }} />
                    <Box>
                      <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5, fontSize: '0.875rem' }}>
                        What You'll Get
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.875rem' }}>
                        Production certificates, secure API integration, and the ability to submit compliant e-invoices to ZATCA.
                      </Typography>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} md={4}>
              <Card elevation={1} sx={{ borderRadius: 1, height: '100%' }}>
                <CardContent sx={{ p: 2.5 }}>
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
                    <Assignment sx={{ color: '#f57c00', mt: 0.5 }} />
                    <Box>
                      <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5, fontSize: '0.875rem' }}>
                        Requirements
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.875rem' }}>
                        Valid OTP from ZATCA portal, company details, and VAT registration number to complete onboarding.
                      </Typography>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </Container>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        minHeight: '100vh',
        background: '#f8f9fa',
        py: 2,
        px: { xs: 1, md: 2 },
        width: '100%',
        overflow: 'auto',
      }}
    >
      <Container maxWidth={false} sx={{ px: { xs: 1, sm: 2 }, width: '100%' }}>
        {/* Header */}
        <Box sx={{ mb: 2 }}>
          <Typography
            variant="h5"
            component="h1"
            fontWeight="600"
            sx={{
              color: '#031b4e',
              mb: 0.5,
              fontSize: { xs: '1.25rem', md: '1.5rem' }
            }}
          >
            ZATCA Compliance Details
          </Typography>
          <Typography color="text.secondary" variant="body2" sx={{ fontSize: '0.875rem' }}>
            View your ZATCA e-invoicing compliance status and certificate information
          </Typography>
        </Box>

        {/* Status Overview Cards */}
        <Grid container spacing={1.5} sx={{ mb: 2 }}>
          <Grid item xs={12} sm={6} md={3}>
            <Card elevation={1} sx={{ borderRadius: 1, height: '100%' }}>
              <CardContent sx={{ p: 2.5 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <Box
                    sx={{
                      width: 48,
                      height: 48,
                      borderRadius: '8px',
                      bgcolor: isPhase2 ? '#2e7d32' : '#1976d2',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    {isPhase2 ? (
                      <CheckCircle sx={{ fontSize: '1.5rem', color: '#ffffff' }} />
                    ) : (
                      <Warning sx={{ fontSize: '1.5rem', color: '#ffffff' }} />
                    )}
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem', textTransform: 'uppercase', fontWeight: 500 }}>
                      Phase Status
                    </Typography>
                    <Typography variant="h6" sx={{ fontWeight: 600, fontSize: '1rem' }}>
                      {isPhase2 ? 'Phase 2' : 'Phase 1'}
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Card elevation={1} sx={{ borderRadius: 1, height: '100%' }}>
              <CardContent sx={{ p: 2.5 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <Box
                    sx={{
                      width: 48,
                      height: 48,
                      borderRadius: '8px',
                      bgcolor: '#4caf50',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <CloudDone sx={{ fontSize: '1.5rem', color: '#ffffff' }} />
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem', textTransform: 'uppercase', fontWeight: 500 }}>
                      Environment
                    </Typography>
                    <Typography variant="h6" sx={{ fontWeight: 600, fontSize: '1rem' }}>
                      {data?.environmentType || 'N/A'}
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Card elevation={1} sx={{ borderRadius: 1, height: '100%' }}>
              <CardContent sx={{ p: 2.5 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <Box
                    sx={{
                      width: 48,
                      height: 48,
                      borderRadius: '8px',
                      bgcolor: '#ff9800',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <Assignment sx={{ fontSize: '1.5rem', color: '#ffffff' }} />
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem', textTransform: 'uppercase', fontWeight: 500 }}>
                      Total Invoices
                    </Typography>
                    <Typography variant="h6" sx={{ fontWeight: 600, fontSize: '1rem' }}>
                      {data?.invoiceStatistics?.totalInvoices || 0}
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Card elevation={1} sx={{ borderRadius: 1, height: '100%' }}>
              <CardContent sx={{ p: 2.5 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <Box
                    sx={{
                      width: 48,
                      height: 48,
                      borderRadius: '8px',
                      bgcolor: '#9c27b0',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <Description sx={{ fontSize: '1.5rem', color: '#ffffff' }} />
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem', textTransform: 'uppercase', fontWeight: 500 }}>
                      Last ICV
                    </Typography>
                    <Typography variant="h6" sx={{ fontWeight: 600, fontSize: '1rem' }}>
                      {data?.invoiceStatistics?.lastICV || 0}
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Upgrade Alert */}
        {!isPhase2 && (
          <Card elevation={1} sx={{ borderRadius: 1, mb: 2, bgcolor: '#fff3e0', border: '1px solid #ffb74d' }}>
            <CardContent sx={{ p: 2.5 }}>
              <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                <Info sx={{ color: '#f57c00', mt: 0.5 }} />
                <Box sx={{ flex: 1 }}>
                  <Typography variant="h6" sx={{ fontWeight: 600, mb: 1, color: '#e65100', fontSize: '0.875rem' }}>
                    Upgrade to Phase 2
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2, fontSize: '0.875rem' }}>
                    Complete the onboarding process to obtain your production certificate and enable full e-invoicing compliance.
                  </Typography>
                  <Button
                    variant="contained"
                    onClick={handleUpgrade}
                    sx={{
                      bgcolor: '#f57c00',
                      '&:hover': { bgcolor: '#e65100' },
                      borderRadius: 1,
                      textTransform: 'none',
                      fontSize: '0.875rem',
                      fontWeight: 500
                    }}
                  >
                    Start Phase 2 Onboarding
                  </Button>
                </Box>
              </Box>
            </CardContent>
          </Card>
        )}

        {/* Readiness Status */}
        {isPhase2 && (
          <Card elevation={1} sx={{ borderRadius: 1, mb: 2, bgcolor: canSubmit ? '#e8f5e9' : '#fff3e0', border: `1px solid ${canSubmit ? '#81c784' : '#ffb74d'}` }}>
            <CardContent sx={{ p: 2.5 }}>
              <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                {canSubmit ? (
                  <CheckCircle sx={{ color: '#2e7d32', mt: 0.5 }} />
                ) : (
                  <Warning sx={{ color: '#f57c00', mt: 0.5 }} />
                )}
                <Box sx={{ flex: 1 }}>
                  <Typography variant="h6" sx={{ fontWeight: 600, mb: 0.5, color: canSubmit ? '#2e7d32' : '#e65100', fontSize: '0.875rem' }}>
                    {canSubmit ? 'Ready for Production' : 'Not Ready for Production'}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.875rem' }}>
                    {canSubmit 
                      ? 'Your system is configured and ready to submit invoices to ZATCA.'
                      : 'Complete all onboarding steps to enable invoice submission.'}
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        )}

        <Grid container spacing={2}>
          {/* Company Details */}
          <Grid item xs={12} md={6}>
            <Card elevation={1} sx={{ borderRadius: 1, height: '100%' }}>
              <CardHeader
                title="Company Details"
                titleTypographyProps={{ variant: 'subtitle1', sx: { fontSize: '0.875rem', fontWeight: 600, color: '#031b4e', textTransform: 'uppercase' } }}
                sx={{ p: 2, pb: 1 }}
              />
              <Divider />
              <CardContent sx={{ p: 2 }}>
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem', textTransform: 'uppercase', fontWeight: 500 }}>
                      Organization Name
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 500, mt: 0.5, fontSize: '0.875rem' }}>
                      {data?.companyDetails?.organizationName || 'N/A'}
                    </Typography>
                  </Grid>
                  <Grid item xs={12}>
                    <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem', textTransform: 'uppercase', fontWeight: 500 }}>
                      Organization Unit
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 500, mt: 0.5, fontSize: '0.875rem' }}>
                      {data?.companyDetails?.organizationUnit || 'N/A'}
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem', textTransform: 'uppercase', fontWeight: 500 }}>
                      VAT Number
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 500, mt: 0.5, fontSize: '0.875rem' }}>
                      {data?.companyDetails?.organizationIdentifier || 'N/A'}
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem', textTransform: 'uppercase', fontWeight: 500 }}>
                      Country
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 500, mt: 0.5, fontSize: '0.875rem' }}>
                      {data?.companyDetails?.country || 'N/A'}
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem', textTransform: 'uppercase', fontWeight: 500 }}>
                      Industry
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 500, mt: 0.5, fontSize: '0.875rem' }}>
                      {data?.companyDetails?.industryCategory || 'N/A'}
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem', textTransform: 'uppercase', fontWeight: 500 }}>
                      Location
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 500, mt: 0.5, fontSize: '0.875rem' }}>
                      {data?.companyDetails?.locationAddress || 'N/A'}
                    </Typography>
                  </Grid>
                  <Grid item xs={12}>
                    <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem', textTransform: 'uppercase', fontWeight: 500 }}>
                      Common Name
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 500, mt: 0.5, fontFamily: 'monospace', fontSize: '0.75rem' }}>
                      {data?.companyDetails?.commonName || 'N/A'}
                    </Typography>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>

          {/* Certificate Status */}
          <Grid item xs={12} md={6}>
            <Card elevation={1} sx={{ borderRadius: 1, height: '100%' }}>
              <CardHeader
                title="Certificate Status"
                titleTypographyProps={{ variant: 'subtitle1', sx: { fontSize: '0.875rem', fontWeight: 600, color: '#031b4e', textTransform: 'uppercase' } }}
                sx={{ p: 2, pb: 1 }}
              />
              <Divider />
              <CardContent sx={{ p: 2 }}>
                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <CheckCircle sx={{ fontSize: '1rem', color: data?.certificates?.hasCSR ? '#4caf50' : '#9e9e9e' }} />
                      <Typography variant="body2" sx={{ fontSize: '0.875rem' }}>CSR Generated</Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={6}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <CheckCircle sx={{ fontSize: '1rem', color: data?.certificates?.hasPrivateKey ? '#4caf50' : '#9e9e9e' }} />
                      <Typography variant="body2" sx={{ fontSize: '0.875rem' }}>Private Key</Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={6}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <CheckCircle sx={{ fontSize: '1rem', color: data?.certificates?.hasComplianceCSID ? '#4caf50' : '#9e9e9e' }} />
                      <Typography variant="body2" sx={{ fontSize: '0.875rem' }}>Compliance CSID</Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={6}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <CheckCircle sx={{ fontSize: '1rem', color: data?.certificates?.hasProductionCSID ? '#4caf50' : '#9e9e9e' }} />
                      <Typography variant="body2" sx={{ fontSize: '0.875rem' }}>Production CSID</Typography>
                    </Box>
                  </Grid>
                  {data?.certificates?.complianceCSID?.requestID && (
                    <Grid item xs={12}>
                      <Divider sx={{ my: 1 }} />
                      <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem', textTransform: 'uppercase', fontWeight: 500 }}>
                        Compliance Request ID
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 500, mt: 0.5, fontFamily: 'monospace', fontSize: '0.75rem' }}>
                        {data.certificates.complianceCSID.requestID}
                      </Typography>
                    </Grid>
                  )}
                  {data?.certificates?.productionCSID?.requestID && (
                    <Grid item xs={12}>
                      <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem', textTransform: 'uppercase', fontWeight: 500 }}>
                        Production Request ID
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 500, mt: 0.5, fontFamily: 'monospace', fontSize: '0.75rem' }}>
                        {data.certificates.productionCSID.requestID}
                      </Typography>
                    </Grid>
                  )}
                  {data?.invoiceStatistics?.lastInvoiceHash && (
                    <Grid item xs={12}>
                      <Divider sx={{ my: 1 }} />
                      <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem', textTransform: 'uppercase', fontWeight: 500 }}>
                        Last Invoice Hash
                      </Typography>
                      <Typography 
                        variant="body2" 
                        sx={{ 
                          fontWeight: 500, 
                          mt: 0.5, 
                          fontFamily: 'monospace', 
                          fontSize: '0.7rem',
                          wordBreak: 'break-all'
                        }}
                      >
                        {data.invoiceStatistics.lastInvoiceHash}
                      </Typography>
                    </Grid>
                  )}
                </Grid>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
};

export default ZatcaDetails;
